import { useEffect, useState } from "react";
import { pro as proApi } from "@/lib/api";

/**
 * useProPricing — single source of truth for the Pro plan amounts shown
 * across the site (CTAs, gates, gated tables, /guide, navbar badge, etc.).
 *
 * Reads from /api/pro/plans which itself reads admin_settings.pro_pricing.
 *
 * Behaviour:
 *  • Module-level cache so we don't refetch on every component mount.
 *  • Module-level subscriber set so ALL mounted consumers re-render when
 *    admin saves new pricing (clearProPricingCache busts cache + refetches +
 *    broadcasts to every subscriber).
 *
 * Returns formatted strings used directly in copy:
 *   { monthly: 999, annual: 9999, monthlyLabel: "₹999/month",
 *     annualLabel: "₹9,999/year", monthlyShort: "₹999/mo",
 *     savings_inr: 1989, savingsLabel: "₹1,989", loading }
 */
const DEFAULTS = {
  monthly: 999,
  annual: 9999,
  monthlyLabel: "₹999/month",
  annualLabel: "₹9,999/year",
  monthlyShort: "₹999/mo",
  annualShort: "₹9,999/yr",
  savings_inr: 1989,
  savingsLabel: "₹1,989",
};

let _cache = null;
let _inFlight = null;
const _subscribers = new Set();

function format(plans) {
  const m = plans?.find((p) => p.id === "monthly")?.price_inr ?? DEFAULTS.monthly;
  const a = plans?.find((p) => p.id === "annual")?.price_inr ?? DEFAULTS.annual;
  const s = plans?.find((p) => p.id === "annual")?.savings_inr ?? Math.max(m * 12 - a, 0);
  return {
    monthly: m,
    annual: a,
    monthlyLabel: `₹${m.toLocaleString("en-IN")}/month`,
    annualLabel: `₹${a.toLocaleString("en-IN")}/year`,
    monthlyShort: `₹${m.toLocaleString("en-IN")}/mo`,
    annualShort: `₹${a.toLocaleString("en-IN")}/yr`,
    savings_inr: s,
    savingsLabel: `₹${s.toLocaleString("en-IN")}`,
  };
}

function broadcast(next) {
  _cache = next;
  _subscribers.forEach((cb) => cb(next));
}

function fetchAndBroadcast() {
  if (_inFlight) return _inFlight;
  _inFlight = proApi
    .plans()
    .then((r) => {
      const v = format(r.plans);
      _inFlight = null;
      broadcast(v);
      return v;
    })
    .catch(() => {
      _inFlight = null;
      broadcast(DEFAULTS);
      return DEFAULTS;
    });
  return _inFlight;
}

export default function useProPricing() {
  const [state, setState] = useState(() =>
    _cache ? { ..._cache, loading: false } : { ...DEFAULTS, loading: !_cache }
  );

  useEffect(() => {
    const cb = (v) => setState({ ...v, loading: false });
    _subscribers.add(cb);

    if (_cache) {
      cb(_cache);
    } else {
      fetchAndBroadcast();
    }
    return () => {
      _subscribers.delete(cb);
    };
  }, []);

  return state;
}

/**
 * Force a refresh — used by Admin Settings save() after persisting new
 * pricing. Busts the cache, refetches /api/pro/plans, and broadcasts the
 * new values to every mounted consumer so the navbar badge, /pro CTAs,
 * /guide section, ProGate buttons and gated-table copy all update live.
 */
export function clearProPricingCache() {
  _cache = null;
  _inFlight = null;
  return fetchAndBroadcast();
}
